// Optional in-page UI. If the embedding document contains an element with
// id="snowflake", the library takes it over and renders a small panel there:
// the Snowstorm logo, a line of connection status, and a toggle to opt out.
//
// Pages that don't want the UI simply omit the element; nothing is injected.
const logo = require('./logo');

const MOUNT_ID = 'snowflake';

// Keyed by the status codes documented at the top of lib.js.
const STATES = {
    0: { label: 'Connecting…', tone: 'pending' },
    1: { label: "Can't reach the network", tone: 'error' },
    2: { label: 'Off — not relaying', tone: 'off' },
    3: { label: 'Finding a route…', tone: 'pending' },
    4: { label: 'Reconnecting…', tone: 'pending' },
    5: { label: 'Connected — ready to relay', tone: 'ok' },
    6: { label: 'Connected — relaying traffic', tone: 'active' },
};

const UNKNOWN = { label: 'Starting…', tone: 'pending' };

const STYLE = `
:host {
    all: initial;
    display: block;
    color: #111111;
    font: 14px/1.4 system-ui, -apple-system, "Segoe UI", Helvetica, Arial, sans-serif;
}
.panel {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
    border: 1px solid rgba(17, 17, 17, 0.12);
    border-radius: 12px;
    background: #ffffff;
}
.logo {
    display: block;
    width: 160px;
    max-width: 100%;
    height: auto;
}
.row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}
.status {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
}
.status-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.dot {
    flex: none;
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: #9aa0a6;
}
.dot[data-tone="ok"], .dot[data-tone="active"] { background: #2e9e5b; }
.dot[data-tone="pending"] { background: #d99a2b; }
.dot[data-tone="error"] { background: #cf3b3b; }
.dot[data-tone="off"] { background: #9aa0a6; }
.dot[data-tone="active"] { animation: pulse 1.6s ease-in-out infinite; }
@keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.35; }
}
.toggle {
    flex: none;
    position: relative;
    width: 40px;
    height: 22px;
    padding: 0;
    border: none;
    border-radius: 11px;
    background: #c7cad1;
    cursor: pointer;
    transition: background 0.15s ease-in-out;
}
.toggle::after {
    content: "";
    position: absolute;
    top: 2px;
    left: 2px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #ffffff;
    transition: transform 0.15s ease-in-out;
}
.toggle[aria-checked="true"] { background: #2e9e5b; }
.toggle[aria-checked="true"]::after { transform: translateX(18px); }
.toggle:focus-visible { outline: 2px solid #111111; outline-offset: 2px; }
@media (prefers-color-scheme: dark) {
    :host { color: #f5f5f5; }
    .panel {
        background: #161616;
        border-color: rgba(245, 245, 245, 0.16);
    }
    .toggle { background: #4a4d54; }
    .toggle:focus-visible { outline-color: #f5f5f5; }
}
@media (prefers-reduced-motion: reduce) {
    .dot, .toggle, .toggle::after { animation: none; transition: none; }
}
`;

// Renders into `element` and returns a handle with an update(status) method.
// The panel lives in a shadow root so the host page's CSS can't disturb it.
function render(element) {
    const root = element.shadowRoot || element.attachShadow({ mode: 'open' });
    root.innerHTML = `
        <style>${STYLE}</style>
        <div class="panel" part="panel">
            <div class="logo">${logo}</div>
            <div class="row">
                <div class="status">
                    <span class="dot" data-tone="pending"></span>
                    <span class="status-text"></span>
                </div>
                <button class="toggle" type="button" role="switch"
                        aria-checked="true" aria-label="Carry Snowstorm traffic"></button>
            </div>
        </div>`;
    return {
        dot: root.querySelector('.dot'),
        text: root.querySelector('.status-text'),
        toggle: root.querySelector('.toggle'),
    };
}

// Mount the status panel if the document opts in with an element carrying the
// mount id. `lib` is the module returned by ./lib (for getStatus/onStatus/
// enable/disable). Returns the mount element, or null when the page has none.
module.exports = (global, lib, id) => {
    const document = global.document;
    if (!document) return null;
    const element = document.getElementById(id || MOUNT_ID);
    if (!element) return null;
    if (element.snowflakeMounted) return element;
    element.snowflakeMounted = true;

    const ui = render(element);

    const paint = (code) => {
        // The toggle tracks the saved preference; the status line tracks the
        // live connection, which churns on its own while enabled.
        const enabled = lib.isEnabled();
        const state = enabled ? (STATES[code] || UNKNOWN) : STATES[2];
        ui.dot.dataset.tone = state.tone;
        ui.text.textContent = state.label;
        ui.text.title = state.label;
        ui.toggle.setAttribute('aria-checked', enabled ? 'true' : 'false');
    };

    ui.toggle.addEventListener('click', () => {
        if (lib.isEnabled()) {
            lib.disable();
        } else {
            lib.enable();
        }
        // enable()/disable() reach a status the listener will also report, but
        // paint now so the toggle responds on the same tick as the click.
        paint(lib.getStatus());
    });

    paint(lib.getStatus());
    lib.onStatus(paint);
    return element;
};

module.exports.MOUNT_ID = MOUNT_ID;
